import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Home, LayoutGrid, Activity as ActivityIcon, Inbox as InboxIcon, Users, ListChecks, AlertTriangle, Settings, Zap, User, Target, CheckSquare } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const PAGES = [
  { to: "/", label: "Home (Morning Brief)", icon: Home },
  { to: "/projects", label: "Projects", icon: LayoutGrid },
  { to: "/activity", label: "Activity", icon: ActivityIcon },
  { to: "/inbox", label: "Inbox", icon: InboxIcon },
  { to: "/people", label: "People", icon: Users },
  { to: "/sops", label: "SOPs", icon: ListChecks },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function CommandPalette({ open, setOpen }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ projects: [], people: [], sops: [], tasks: [], escalations: [] });

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((o) => !o); }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [projects, people, sops, tasks, escalations] = await Promise.all([
          endpoints.projects(), endpoints.people(), endpoints.sops(), endpoints.tasks?.() || Promise.resolve([]), endpoints.escalations(),
        ]);
        setData({ projects, people, sops, tasks, escalations });
      } catch (e) { /* ignore */ }
    })();
  }, [open]);

  // Navigate + set focus= param so destination page can scroll & highlight
  const go = (to, focus) => {
    setOpen(false);
    navigate(focus ? `${to}${to.includes("?") ? "&" : "?"}focus=${focus}` : to);
  };

  const loadDemo = async () => {
    setOpen(false);
    try { await endpoints.demoReset(); toast.success("Demo data loaded"); window.location.reload(); }
    catch (e) { toast.error("Failed to load demo"); }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, projects, tasks, people, SOPs…" data-testid="command-palette-input" />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {PAGES.map((p) => (
            <CommandItem key={p.to} onSelect={() => go(p.to)} data-testid={`palette-page-${p.label.toLowerCase().split(" ")[0]}`}>
              <p.icon size={14} className="mr-2" />
              {p.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem onSelect={loadDemo} data-testid="palette-action-demo">
            <Zap size={14} className="mr-2" />
            Load demo data
          </CommandItem>
        </CommandGroup>
        {data.projects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Projects">
              {data.projects.map((p) => (
                <CommandItem key={p.id} onSelect={() => go("/projects", p.id)} value={`project ${p.name}`} data-testid={`palette-project-${p.id}`}>
                  <Target size={14} className="mr-2" />{p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {(data.tasks || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Tasks">
              {data.tasks.slice(0, 25).map((t) => {
                const proj = data.projects.find((p) => p.id === t.project_id);
                return (
                  <CommandItem
                    key={t.id}
                    onSelect={() => { setOpen(false); navigate(`/projects?focus=${t.project_id}&highlight=task-${t.id}`); }}
                    value={`task ${t.title} ${proj?.name || ""}`}
                  >
                    <CheckSquare size={14} className="mr-2" />
                    {t.title}
                    {proj && <span className="ml-2 text-xs opacity-60">in {proj.name}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
        {data.people.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="People">
              {data.people.map((p) => (
                <CommandItem key={p.id} onSelect={() => go("/people", p.id)} value={`person ${p.name} ${p.role || ""}`} data-testid={`palette-person-${p.id}`}>
                  <User size={14} className="mr-2" />
                  {p.name}<span className="ml-2 text-xs opacity-60">{p.role}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {data.escalations.filter((e) => e.status === "pending").length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Escalations">
              {data.escalations.filter((e) => e.status === "pending").map((e) => (
                <CommandItem key={e.id} onSelect={() => go("/activity", e.id)} value={`escalation ${e.reason}`}>
                  <AlertTriangle size={14} className="mr-2 text-[#FF453A]" />
                  {e.reason}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {data.sops.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="SOPs">
              {data.sops.map((s) => (
                <CommandItem key={s.id} onSelect={() => go("/sops", s.id)} value={`sop ${s.name}`}>
                  <ListChecks size={14} className="mr-2" />{s.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
